/* === ADDON: hub-flock — ambient "runner current" canvas layer ============
   A school-of-fish-style flow field, rebuilt with tiny sprinting runner
   figures (track-pictogram style). Auto-mounts a canvas inside every
   element carrying [data-hub-flock] and animates agents along a current:

     data-hub-flock="vortex"   circular school swirling around a centre
                    "stream"   lanes flowing horizontally across the box
                    "orbit8"   two counter-rotating lobes (figure-eight)
     data-flock-dir="cw|ccw"   swirl / flow direction        (default cw)
     data-flock-n="80"         agent count                   (default 80)
     data-flock-opacity="0.5"  overall layer opacity         (default 0.5)
     data-flock-scale="1"      size multiplier               (default 1)
     data-flock-sports="run,cycle,swim,hyrox"  glyph mix (repeat a sport to
                    weight it; default "run,run,cycle,swim,hyrox")

   Perf: DPR capped at 2, one rAF per mount, drawing skipped while the host
   is hidden (closed mega menu) or off-screen. Reduced motion renders one
   static frame. Remove this file + the [data-hub-flock] attributes + the
   hub-flock CSS fence to retire. ========================================= */
(function () {
  'use strict';
  var reduceMotion = window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches;

  var LIME = [183, 255, 0];
  var WHITE = [240, 255, 214];
  var OLIVE = [120, 160, 30];

  function mount(host) {
    if (host.__flock) return;
    host.__flock = true;
    if (getComputedStyle(host).position === 'static') host.style.position = 'relative';

    var canvas = document.createElement('canvas');
    canvas.className = 'hub-flock';
    canvas.setAttribute('aria-hidden', 'true');
    host.insertBefore(canvas, host.firstChild);
    var ctx = canvas.getContext('2d');
    if (!ctx) return;

    var mode = host.getAttribute('data-hub-flock') || 'vortex';
    var dir = (host.getAttribute('data-flock-dir') === 'ccw') ? -1 : 1;
    var N = Math.max(10, parseInt(host.getAttribute('data-flock-n') || '80', 10));
    var alpha = parseFloat(host.getAttribute('data-flock-opacity') || '0.5');
    var sizeMul = parseFloat(host.getAttribute('data-flock-scale') || '1');
    var sports = (host.getAttribute('data-flock-sports') || 'run,run,cycle,swim,hyrox')
      .split(',').map(function (s) { return s.trim(); }).filter(Boolean);
    // per-sport speed and limb-cadence multipliers (cyclists fly, sleds grind)
    var SPORT = {
      run:   { spd: 1.0,  cad: 1.0 },
      cycle: { spd: 1.55, cad: 0.9 },
      swim:  { spd: 0.75, cad: 0.55 },
      hyrox: { spd: 0.6,  cad: 0.8 }
    };
    canvas.style.opacity = alpha;

    var dpr = Math.min(window.devicePixelRatio || 1, 2);
    var w = 0, h = 0;
    function size() {
      var r = host.getBoundingClientRect();
      if (r.width < 4 || r.height < 4) return;         // hidden panel: keep old buffer
      w = canvas.width = Math.floor(r.width * dpr);
      h = canvas.height = Math.floor(r.height * dpr);
    }
    size();
    if (window.ResizeObserver) new ResizeObserver(size).observe(host);

    /* --- agents ------------------------------------------------------- */
    var A = [];
    function spawn(a, anywhere) {
      a.seed = Math.random() * Math.PI * 2;
      a.sc = (9 + Math.random() * 8) * sizeMul * dpr;   // glyph size px
      a.sp = (0.5 + Math.random() * 0.7);               // speed factor
      a.phase = Math.random() * Math.PI * 2;            // stride cycle
      a.tone = Math.random();                            // colour pick
      a.rOff = 0.55 + Math.random() * 0.6;              // vortex ring spread
      var pick = sports[(Math.random() * sports.length) | 0];
      a.sport = SPORT[pick] ? pick : 'run';
      if (mode === 'stream') {
        a.x = anywhere ? Math.random() * w : (dir > 0 ? -0.05 * w : 1.05 * w);
        a.y = Math.random() * h;
      } else {
        var cx = w / 2, cy = h * 0.52;
        var R = Math.min(w, h) * 0.33 * a.rOff;
        var an = Math.random() * Math.PI * 2;
        a.x = cx + Math.cos(an) * R;
        a.y = cy + Math.sin(an) * R;
      }
      a.hd = Math.random() * Math.PI * 2;               // heading
    }
    for (var i = 0; i < N; i++) { var a = {}; spawn(a, true); A.push(a); }

    /* --- flow field: desired heading at (x, y) ------------------------ */
    function field(a, t) {
      var cx = w / 2, cy = h * 0.52;
      if (mode === 'stream') {
        // lanes flowing across with a slow sine weave
        return (dir > 0 ? 0 : Math.PI) + Math.sin(t * 0.0006 + a.seed + a.y * 0.008 / dpr) * 0.3;
      }
      if (mode === 'orbit8') {
        // two counter-rotating lobes -> emergent figure-eight
        var lx = a.x < cx ? cx - w * 0.22 : cx + w * 0.22;
        var s = a.x < cx ? dir : -dir;
        var dx = a.x - lx, dy = a.y - cy;
        return Math.atan2(dy, dx) + s * (Math.PI / 2) - s * 0.22;
      }
      // vortex: tangent around centre + gentle pull to each agent's ring
      var vx = a.x - cx, vy = a.y - cy;
      var an = Math.atan2(vy, vx);
      var d = Math.sqrt(vx * vx + vy * vy) || 1;
      var R = Math.min(w, h) * 0.33 * a.rOff;
      var radial = (R - d) / R;                          // + = drift outward
      return an + dir * (Math.PI / 2) + Math.max(-0.6, Math.min(0.6, radial * 0.9)) * dir
             + Math.sin(t * 0.0005 + a.seed) * 0.16;    // school wobble
    }

    /* --- tiny sprinting figure: always UPRIGHT, mirrored to face its
       direction of travel, with a slight lean into climbs/descents. The
       motion streak is drawn separately along the TRUE heading so the
       current's direction stays readable. ------------------------------ */
    function runner(a, col) {
      var s = a.sc, ph = a.phase;
      var swing = Math.sin(ph), counter = Math.sin(ph + Math.PI);
      var tx = Math.cos(a.hd), ty = Math.sin(a.hd);
      // comet tail in world space (points opposite the real movement)
      var g = ctx.createLinearGradient(a.x - tx * 2.4 * s, a.y - ty * 2.4 * s, a.x - tx * 0.3 * s, a.y - ty * 0.3 * s);
      g.addColorStop(0, 'rgba(0,0,0,0)'); g.addColorStop(1, col);
      ctx.strokeStyle = g;
      ctx.lineWidth = 0.14 * s; ctx.lineCap = 'round'; ctx.lineJoin = 'round';
      ctx.beginPath();
      ctx.moveTo(a.x - tx * 2.4 * s, a.y - ty * 2.4 * s);
      ctx.lineTo(a.x - tx * 0.3 * s, a.y - ty * 0.3 * s);
      ctx.stroke();
      // upright glyph: flip to face travel, lean forward uphill / back downhill
      var face = tx >= 0 ? 1 : -1;
      var tilt = -ty * face * 0.32;
      ctx.save();
      ctx.translate(a.x, a.y);
      ctx.rotate(tilt);
      ctx.scale(face * s, s);
      ctx.strokeStyle = col; ctx.fillStyle = col;
      ctx.lineWidth = 0.16; ctx.lineCap = 'round'; ctx.lineJoin = 'round';
      if (a.sport === 'cycle') glyphCycle(ph);
      else if (a.sport === 'swim') glyphSwim(ph, swing);
      else if (a.sport === 'hyrox') glyphHyrox(swing, counter);
      else glyphRun(swing, counter);
      ctx.restore();
    }

    /* sprinting figure */
    function glyphRun(swing, counter) {
      // torso (forward lean) + head
      ctx.beginPath(); ctx.moveTo(-0.18, 0.16); ctx.lineTo(0.16, -0.32); ctx.stroke();
      ctx.beginPath(); ctx.arc(0.30, -0.46, 0.13, 0, 6.2832); ctx.fill();
      // legs: hip at (-0.18,0.16), stride cycle
      ctx.beginPath();
      ctx.moveTo(-0.18, 0.16); ctx.lineTo(0.16 + 0.22 * swing, 0.5); ctx.lineTo(0.5 + 0.3 * swing, 0.62 + 0.12 * swing);
      ctx.moveTo(-0.18, 0.16); ctx.lineTo(-0.34 + 0.2 * counter, 0.52); ctx.lineTo(-0.6 + 0.26 * counter, 0.78 - 0.1 * counter);
      ctx.stroke();
      // arms: shoulder at (0.1,-0.24), counter-swing
      ctx.beginPath();
      ctx.moveTo(0.1, -0.24); ctx.lineTo(0.38 + 0.18 * counter, -0.02);
      ctx.moveTo(0.1, -0.24); ctx.lineTo(-0.2 + 0.18 * swing, -0.06);
      ctx.stroke();
    }

    /* cyclist: two wheels + frame + tucked rider, pedals turn with phase */
    function glyphCycle(ph) {
      var px = Math.cos(ph) * 0.15, py = Math.sin(ph) * 0.15;   // pedal crank
      ctx.beginPath(); ctx.arc(-0.52, 0.45, 0.3, 0, 6.2832); ctx.stroke(); // rear wheel
      ctx.beginPath(); ctx.arc(0.52, 0.45, 0.3, 0, 6.2832); ctx.stroke();  // front wheel
      // frame: rear hub → crank → front hub; crank → seat; front hub → bars
      ctx.beginPath();
      ctx.moveTo(-0.52, 0.45); ctx.lineTo(0, 0.42); ctx.lineTo(0.52, 0.45);
      ctx.moveTo(0, 0.42); ctx.lineTo(-0.14, 0.02);
      ctx.moveTo(0.52, 0.45); ctx.lineTo(0.36, -0.02);
      ctx.stroke();
      // rider: seat → shoulders tuck, head, arms to bars
      ctx.beginPath(); ctx.moveTo(-0.14, 0.02); ctx.lineTo(0.22, -0.3); ctx.stroke();
      ctx.beginPath(); ctx.arc(0.36, -0.36, 0.12, 0, 6.2832); ctx.fill();
      ctx.beginPath(); ctx.moveTo(0.22, -0.3); ctx.lineTo(0.38, -0.04); ctx.stroke();
      // legs to the two pedal positions (opposite crank sides)
      ctx.beginPath();
      ctx.moveTo(-0.14, 0.02); ctx.lineTo(px, 0.42 + py);
      ctx.moveTo(-0.14, 0.02); ctx.lineTo(-px, 0.42 - py);
      ctx.stroke();
    }

    /* swimmer: horizontal freestyle — windmill arm, flutter kick */
    function glyphSwim(ph, swing) {
      // body line with a slight undulation + head
      ctx.beginPath(); ctx.moveTo(-0.55, 0.1 + 0.04 * swing); ctx.quadraticCurveTo(-0.05, -0.06, 0.42, 0.02); ctx.stroke();
      ctx.beginPath(); ctx.arc(0.56, 0.0, 0.12, 0, 6.2832); ctx.fill();
      // windmill arm from the shoulder (full circle with phase)
      var ax = 0.3, ay = 0.0;
      ctx.beginPath();
      ctx.moveTo(ax, ay);
      ctx.lineTo(ax + Math.cos(ph) * 0.34, ay + Math.sin(ph) * 0.34);
      ctx.stroke();
      // flutter kick: two short legs scissoring at the tail
      ctx.beginPath();
      ctx.moveTo(-0.55, 0.1); ctx.lineTo(-0.82, 0.16 + 0.14 * swing);
      ctx.moveTo(-0.55, 0.1); ctx.lineTo(-0.8, 0.04 - 0.14 * swing);
      ctx.stroke();
    }

    /* hyrox: sled push — hard forward lean, driving legs, sled up front */
    function glyphHyrox(swing, counter) {
      // sled: box + push pole
      ctx.strokeRect(0.48, 0.34, 0.34, 0.26);
      ctx.beginPath(); ctx.moveTo(0.52, 0.34); ctx.lineTo(0.44, -0.08); ctx.stroke();
      // athlete: deep lean, hip → shoulder, head low
      ctx.beginPath(); ctx.moveTo(-0.34, 0.22); ctx.lineTo(0.16, -0.12); ctx.stroke();
      ctx.beginPath(); ctx.arc(0.28, -0.2, 0.11, 0, 6.2832); ctx.fill();
      // both arms locked onto the pole
      ctx.beginPath();
      ctx.moveTo(0.16, -0.12); ctx.lineTo(0.44, -0.06);
      ctx.moveTo(0.16, -0.12); ctx.lineTo(0.42, 0.02);
      ctx.stroke();
      // driving legs (short powerful steps)
      ctx.beginPath();
      ctx.moveTo(-0.34, 0.22); ctx.lineTo(-0.12 + 0.14 * swing, 0.5); ctx.lineTo(0.08 + 0.18 * swing, 0.72);
      ctx.moveTo(-0.34, 0.22); ctx.lineTo(-0.5 + 0.12 * counter, 0.52); ctx.lineTo(-0.66 + 0.16 * counter, 0.76);
      ctx.stroke();
    }

    function colorOf(a, fade) {
      var c = a.tone < 0.18 ? WHITE : (a.tone < 0.72 ? LIME : OLIVE);
      var o = (0.35 + 0.65 * Math.abs(Math.sin(a.seed * 3 + a.phase * 0.13))) * fade;
      return 'rgba(' + c[0] + ',' + c[1] + ',' + c[2] + ',' + o.toFixed(3) + ')';
    }

    function step(t) {
      ctx.clearRect(0, 0, w, h);
      for (var i = 0; i < N; i++) {
        var a = A[i];
        var want = field(a, t);
        // steer smoothly toward the field direction (shortest arc)
        var diff = Math.atan2(Math.sin(want - a.hd), Math.cos(want - a.hd));
        a.hd += diff * 0.06;
        var sport = SPORT[a.sport] || SPORT.run;
        var v = a.sp * a.sc * 0.06 * sport.spd;
        a.x += Math.cos(a.hd) * v;
        a.y += Math.sin(a.hd) * v;
        a.phase += v / a.sc * 7 * sport.cad;            // stride/stroke tied to travel
        // respawn when far outside
        if (a.x < -0.12 * w || a.x > 1.12 * w || a.y < -0.12 * h || a.y > 1.12 * h) spawn(a);
        runner(a, colorOf(a, 1));
      }
    }

    if (reduceMotion || window.HUB_LITE) {               // one static frame (lite: no rAF loop)
      step(0);
      return;
    }

    var frame = 0;
    var visible = true;
    (function tick(t) {
      requestAnimationFrame(tick);
      // cheap visibility gate every 30 frames (closed mega menu, off-screen)
      if ((frame++ % 30) === 0) {
        var r = host.getBoundingClientRect();
        var style = getComputedStyle(host);
        visible = r.width > 4 && r.bottom > 0 && r.top < window.innerHeight &&
                  style.visibility !== 'hidden' && parseFloat(style.opacity) > 0.02;
        if (visible && (w < 4 || h < 4)) size();         // panel just opened
      }
      if (!visible || w < 4) return;
      step(t);
    })(0);
  }

  function init() {
    document.querySelectorAll('[data-hub-flock]').forEach(mount);
  }
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', init);
  else init();
})();
/* === END ADDON: hub-flock === */
